<template>
  <div>
    <InputFieldLabel
      :label="label"
      :inputActive="inputActive"
      :isDropdown="true"
      class=""
    >
      <div class="tw-relative">
        <select
          :ref="name"
          :name="name"
          :disabled="disabled"
          :id="name"
          :required="required"
          v-model="value"
          class="form__select tw-w-full tw-rounded tw-pl-5 tw-text-secondary tw-h-12"
          :class="[
            v$.value.$error ? 'form__select--error' : null,
            border ? border : 'form__select--default',
          ]"
          @blur="!value || value.length === 0 ? (inputActive = false) : null"
        >
          <option value="" disabled selected>{{ placeholder || 'Select ' + label }}</option>
          <template v-if="isObject">
            <option
              v-for="industry in options"
              :key="industry[name]"
              :value="industry[objectValue.value]"
            >
              {{ industry[objectValue.label] }}
            </option>
          </template>
          <option
            v-else
            v-for="industry in options"
            :key="industry"
            :value="industry"
          >
            {{ industry }}
          </option>
        </select>
        <img
          src="https://res.cloudinary.com/djalafcj9/image/upload/v1653918409/getequityV2/Frame_jhtrqy.svg"
          class="tw-absolute tw-right-2 sm:tw-right-4 tw-top-3"
          alt="chevron icon"
          v-if="!value"
        />
      </div>
    </InputFieldLabel>
    <p class="tw-text-red-error tw-font-normal" v-if="v$.value.$error">
      {{ v$.$errors[0].$message }}
    </p>
  </div>
</template>

<script>
  import InputFieldLabel from "./InputFieldLabel";
  import { required } from "@vuelidate/validators";
  import useValidate from "@vuelidate/core";
  export default {
    name: "SelectDropdown",
    components: {
      InputFieldLabel,
    },
    data() {
      return {
        inputActive: false,
        v$: useValidate(),
        value: "",
      };
    },
    validations() {
      return {
        value: {
          required,
        },
      };
    },
    props: {
      name: {
        type: String,
        required: true,
        default: () => "",
      },
      objectValue: {
        type: Object,
        required: false,
        default: () => {},
      },
      required: {
        type: Boolean,
        required: false,
        default: false,
      },
      isObject: {
        type: Boolean,
        required: false,
        default: false,
      },
      label: {
        type: String,
      },
      disabled: {
        type: Boolean,
        required: false,
      },
      validate: {
        type: Boolean,
        required: true,
      },
      currentValue: {
        type: String,
        required: false,
        default: () => "",
      },
      border: {
        type: String,
        required: false,
      },
      placeholder: {
        type: String,
        required: false,
        default: "",
      },
      //border color class for the input
      options: {
        type: Array,
        required: true,
        default: () => [],
      },
    },
    watch: {
      value(val) {
        if (val && val.length > 0) {
          this.inputActive = true;
          this.$emit("input");
          this.isValid = true;
        }
        if (!val) {
          this.isValid = false;
        }
        this.$emit("input-changed", { name: this.name, value: val });
      },
      currentValue(val) {
        const next = val ?? "";
        if (next !== this.value) {
          this.value = next;
        }
      },
      validate(val) {
        if (val) {
          this.validateInput();
        }
      },
    },
    emits: ["input-changed", "input", "is-valid"],
    methods: {
      validateInput() {
        this.v$.value.$touch();
        this.isValid = !this.v$.value.$error ? true : false;
        this.$emit("is-valid", this.isValid);
      },
    },
    mounted() {
      this.isValid = this.value ? true : false;
      // Normalised to "" so an unset field still matches the disabled placeholder <option>.
      // A null currentValue matches no option at all, and a <select> with no matching option
      // renders blank — no placeholder, no floating label, nothing telling you what it is.
      this.value = this.currentValue ?? "";
    },
  };
</script>

<style lang="scss" scoped>
  // Enterprise Professional Design System
  $text-primary: #0F172A;
  $text-secondary: #475569;
  $text-placeholder: #94A3B8;
  $border-color: #CBD5E1;
  $border-focus: #001240;
  $border-error: #DC2626;
  $background-white: #FFFFFF;
  $shadow-focus: 0 0 0 3px rgba(0, 18, 64, 0.08);

  .form {
    &__select {
      z-index: 1;
      position: absolute;
      transition: border-color 150ms ease, box-shadow 150ms ease;
      appearance: none;
      background-color: $background-white;
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%2364748B' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
      background-position: right 12px center;
      background-repeat: no-repeat;
      background-size: 20px;
      padding-right: 40px;
      font-size: 14px;
      font-weight: 450;
      color: $text-primary;
      border-radius: 8px;
      cursor: pointer;
      
      & option:disabled {
        color: $text-placeholder;
      }
      
      &--default {
        border: 1.5px solid $border-color;
        
        &:hover:not(:focus):not(:disabled) {
          border-color: #94A3B8;
        }
      }
      
      &--error {
        border: 1.5px solid $border-error;
        
        &:focus {
          box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
        }
      }
      
      &:focus {
        outline: none;
        border-color: $border-focus;
        box-shadow: $shadow-focus;
      }
      
      &:disabled {
        background-color: #F1F5F9;
        cursor: not-allowed;
        color: $text-secondary;
      }
    }
  }
</style>
